import { useEffect, useRef } from 'react'
import { captureException } from '@sentry/react-native'
import { resolveHistoricalRetryPayment } from '@ca/common'
import type { HistoricalPaymentGroupMember } from '@ca/common'
import {
  isDeclinedOrderPaymentStatus,
  isSuccessfulOrderPaymentStatus,
  ErpOrderState,
} from '@ca/common/types'
import type { ErpOrderDetail } from '@ca/common/types'
import { trpc } from 'api'
import { useCurrencyDecimals } from 'hooks/useCurrency'
import { getHistoricalPaymentGroupQuery } from './historicalPaymentGroup'

export type GroupPaymentBanner = {
  variant: 'partiallyPaid' | 'declined' | 'nonPayment'
  remaining: number
}

const hiddenBannerReasons = new Set([
  'canceled',
  'nothing_to_pay',
  'active_payment',
  'unsupported_payment',
])

const getPaymentBanner = (
  members: readonly HistoricalPaymentGroupMember[],
  remaining: number
): GroupPaymentBanner => {
  const payments = members.flatMap((member) => member.payments)
  const hasCollectedPayment = payments.some(
    (payment) =>
      isSuccessfulOrderPaymentStatus(payment.status) && payment.amount > 0
  )
  if (hasCollectedPayment) {
    return { variant: 'partiallyPaid', remaining }
  }
  const hasDeclined = payments.some((payment) =>
    isDeclinedOrderPaymentStatus(payment.status)
  )
  return { variant: hasDeclined ? 'declined' : 'nonPayment', remaining }
}

export const useOrderGroupPayments = (order: ErpOrderDetail) => {
  const decimals = useCurrencyDecimals()
  const isCanceled = order.status === ErpOrderState.cancel
  const reportedIssues = useRef(new Set<string>())
  const query = trpc.order.history.useQuery(
    getHistoricalPaymentGroupQuery(order),
    {
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: !isCanceled,
    }
  )
  const hasFreshData =
    !isCanceled &&
    query.isSuccess &&
    query.isFetchedAfterMount &&
    !query.isFetching
  const result =
    hasFreshData && query.data
      ? resolveHistoricalRetryPayment({
          selected: order,
          groupLookup: {
            status: 'ok',
            members: query.data.data,
            hasMore: query.data.meta.nextCursor != null,
          },
          decimals,
        })
      : null
  const failureReason = result && !result.ok ? result.reason : null

  useEffect(() => {
    if (isCanceled) {
      return
    }
    const error =
      query.error ??
      (failureReason === 'inconsistent'
        ? new Error('Invalid historical payment group')
        : null)
    if (!error) {
      return
    }
    const issueKey = `${order.id}:${error.message}`
    if (reportedIssues.current.has(issueKey)) {
      return
    }
    reportedIssues.current.add(issueKey)
    captureException(error, {
      tags: { location: 'historical-payment/overview' },
      extra: { orderId: order.id },
    })
  }, [query.error, failureReason, order.id, isCanceled])

  const banner =
    result?.ok && query.data
      ? getPaymentBanner(query.data.data, result.amount)
      : null

  const shouldHide =
    isCanceled ||
    (failureReason !== null && hiddenBannerReasons.has(failureReason))

  return { isPayable: result?.ok === true, banner, shouldHide }
}
